import {Customer} from "../../api/model/customer";
import {DateTime} from "luxon";
import {TravelComponentsState} from "./travel-components";
import {JourneyState} from "./journey";
import cloneDeep from "lodash/cloneDeep";
import {
    isActivityCarBusState,
    isActivityFlightState,
    isActivityHotelState,
    isCreateOrUpdateBundleMultipleRowActivityResponse,
    isCreateOrUpdateBundleSingleRowActivityResponse
} from "../../utils/check/type-guards";
import {ActivityTypeToIdsMap,} from "../../api/model/bundle";
import {WHICH_ACTION_BUNDLE} from "../../container/buttons/save-bundles-button/SaveBundlesButton";
import {ACTIVITY_TYPE, AllActivityTypesArray} from "../../types-enums/activity";
import {AgencyProps} from "../../api/model/agency";

/**
 * Props shared each element is a bundle, currently travel components and journey.
 */
export interface CommonBundleProps {
    bundleId: number | null,
    userId: string | null,
    name: string | number | null,
    description: string | null,
    agency: AgencyProps | null,
    customer: Customer | null,
    created: DateTime | null,
    updated: DateTime | null,
}

export interface UpdateDataOnCreateUpdateBundle {
    bundleProps: CommonBundleProps,
    mapOfActivityIds: ActivityTypeToIdsMap,
    whichAction: WHICH_ACTION_BUNDLE
}

export const updateHideLoadBundleInformation = (activities: AllActivityTypesArray, activityId: string, value: boolean,) => cloneDeep(activities)
    .map(activity => {
        if (activity.common.id === activityId) activity.common.hideLoadBundleInformation = value
        return activity
    })


/**
 * Updates the common bundle props that must be updated by either the creation of a bundle or the update of an existing
 * bundle.
 */
export const updateCommonBundleProps = (action: UpdateDataOnCreateUpdateBundle, state: TravelComponentsState | JourneyState) => {
    const {bundleProps, whichAction} = action
    const {
        bundleId,
        userId,
        name,
        description,
        agency,
        customer,
        created,
        updated
    } = bundleProps
    switch (whichAction) {
        case WHICH_ACTION_BUNDLE.update:
            state.updated = updated
            break;
        case WHICH_ACTION_BUNDLE.create:
            state.bundleId = bundleId
            state.created = created
            break;
        default:
            whichAction satisfies never
    }
    state.userId = userId
    state.name = name
    state.description = description
    state.agency = agency
    state.customer = customer
    return state
}


/**
 * When a bundle is created, the backend returns among other properties the ID of the created bundle as well as the ID's
 * of the Activities that get created. This function will override every ID from every Activity as well as the ID from the Bundle.
 */
export const updateBundlePropsOnCreateOrUpdate = (
    action: UpdateDataOnCreateUpdateBundle,
    state: TravelComponentsState | JourneyState
) => {
    const {mapOfActivityIds} = action
    state = updateCommonBundleProps(action, state)
    const updatedActivities = cloneDeep(state.activities);
    (Object.keys(mapOfActivityIds) as Array<ACTIVITY_TYPE>).forEach(activityType => {
        const newIdsByActivityType = mapOfActivityIds[activityType]
        const activitiesByActivityType = updatedActivities.filter(updatedActivity => updatedActivity.common.activityType === activityType)
        activitiesByActivityType.forEach(activity => {
            const {common: {activityType}} = activity
            switch (activityType) {
                case ACTIVITY_TYPE.flight:
                    if (isActivityFlightState(activity)) {
                        const {legs} = activity
                        newIdsByActivityType.forEach(activityMapping => {
                            if (isCreateOrUpdateBundleMultipleRowActivityResponse(activityMapping)) {
                                const {frontendIds, backendId, groupId} = activityMapping
                                frontendIds.forEach(frontendId => {
                                    legs.forEach(leg => {
                                        // we must check fo backendId === null because in the backend, one row has departure and arrival airport
                                        // so one leg is "grouped" into one row. While in the FE each activity is its own "airport",
                                        // there is no grouping
                                        if (leg.id === frontendId && leg.backendId === null) {
                                            leg.backendId = backendId
                                            leg.groupId = groupId
                                        }
                                    })
                                })
                            }
                        });
                    }
                    break;
                case ACTIVITY_TYPE.hotel:
                    if (isActivityHotelState(activity)) {
                        newIdsByActivityType.forEach(activityMapping => {
                            if (isCreateOrUpdateBundleSingleRowActivityResponse(activityMapping)) {
                                const {frontendIds, backendId} = activityMapping
                                if (frontendIds.includes(activity.common.id)) activity.backendId = backendId
                            }
                        });
                    }
                    break;
                case ACTIVITY_TYPE.car_bus:
                    if (isActivityCarBusState(activity)) {
                        newIdsByActivityType.forEach(activityMapping => {
                            if (isCreateOrUpdateBundleSingleRowActivityResponse(activityMapping)) {
                                const {frontendIds, backendId} = activityMapping
                                if (frontendIds.includes(activity.common.id)) activity.backendId = backendId
                            }
                        })
                    }
                    break;
                default:
                    activityType satisfies never
            }
        })
    })

    state.activities = updatedActivities
    return state
}

/**
 * Removes bundleId, customer and agency from a journey or a bundle
 */
export const updateBundleOnDeleteAgency = (state: TravelComponentsState | JourneyState, agency: AgencyProps) => {
    if (state.agency?.id === agency.id) {
        state.agency = null
        state.customer = null
        state.bundleId = null
    }
}